/**
 * Taking a newer app when one has been deployed.
 *
 * Asking the service worker to re-read itself is not enough on its own, and that is the whole
 * reason this file exists: `sw.js` is usually byte-for-byte the same from one deploy to the
 * next, so the browser sees nothing new and goes on serving an app that has changed. The
 * document is the file that names the current bundle — it is the only one that always differs
 * — so it is the one worth asking about.
 *
 * Reloading is safe at any moment: IndexedDB and the outbox both survive it, so no change the
 * user has made can be lost this way. What a reload can cost is a half-typed name in the add
 * sheet, which is why the check runs when the app is opened rather than on a timer.
 */

import { whenOpened } from './foreground';

/** Published `no-cache`, so asking for it gets an answer from the server. */
const SHELL = '/index.html';

export function watchForUpdates(): () => void {
  if (!import.meta.env.PROD) {
    // In development the document names a module Vite is still hot-replacing; reloading on
    // that would fight the dev server.
    return () => undefined;
  }

  return whenOpened(() => {
    void takeANewerApp();
  });
}

/**
 * Whether the document on the server names an app bundle this page is not running.
 *
 * Compared as the attributes were written rather than as resolved URLs, because both sides are
 * emitted by the same build and a mismatch in form would read as a mismatch in substance.
 */
export function namesADifferentBundle(
  deployedHtml: string,
  running: readonly string[],
): boolean {
  const deployed = scriptSources(deployedHtml);

  // Nothing recognisable came back — an error page, or a captive portal between us and the
  // server. Reloading into that would take the app away and give nothing back.
  if (deployed.length === 0) {
    return false;
  }

  return deployed.some((source) => !running.includes(source));
}

async function takeANewerApp(): Promise<void> {
  void refreshTheWorker();

  if (await aNewerAppIsDeployed()) {
    reloadOnce();
  }
}

async function aNewerAppIsDeployed(): Promise<boolean> {
  try {
    const response = await fetch(SHELL, { cache: 'no-store' });
    if (!response.ok) {
      return false;
    }

    return namesADifferentBundle(await response.text(), runningScriptSources());
  } catch {
    // Offline, which is an ordinary state here and not a failure. The app on this device is
    // the app it has, and the next opening asks again.
    return false;
  }
}

/**
 * Keeps the worker itself current.
 *
 * Separate from the question above and much rarer: this matters only when `sw.js` has actually
 * changed — new caching rules, or a change to how a reminder is drawn.
 */
async function refreshTheWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    await registration?.update();
  } catch {
    // As above. An app that failed to check for a newer one still works.
  }
}

function runningScriptSources(): string[] {
  return [...document.querySelectorAll('script[src]')].map(
    (script) => script.getAttribute('src') ?? '',
  );
}

function scriptSources(html: string): string[] {
  const tag = /<script[^>]*\ssrc="([^"]+)"/g;
  const sources: string[] = [];

  for (const match of html.matchAll(tag)) {
    sources.push(match[1] ?? '');
  }

  return sources;
}

let reloading = false;

/** Both signals for an opening can arrive; reloading twice is a visible stutter. */
function reloadOnce(): void {
  if (reloading) {
    return;
  }

  reloading = true;
  window.location.reload();
}
